import { useState, useEffect } from "react";
import { fetchAdmin } from "../../utils/adminApi";
import ImageUpload from "../ImageUpload";
import { Button } from "../ui/button";
import { Trash2, Image, Copy, ExternalLink } from "lucide-react";
import { toast } from "sonner";

export default function MediaPanel() {
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => { load(); }, []);

  async function load() {
    try {
      const res = await fetchAdmin("/api/admin/media");
      if (res.ok) setFiles(await res.json());
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }

  async function handleDelete(fileId) {
    if (!window.confirm("Delete this file?")) return;
    const res = await fetchAdmin(`/api/admin/media/${fileId}`, { method: "DELETE" });
    if (res.ok) {
      toast.success("File deleted");
      load();
    } else {
      toast.error("Failed to delete");
    }
  }

  function copyUrl(url) {
    navigator.clipboard.writeText(url);
    toast.success("URL copied to clipboard");
  }

  if (loading) return <div className="flex justify-center py-12"><div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-amber-500" /></div>;

  return (
    <div className="space-y-6" data-testid="media-panel">
      <div className="flex justify-between items-center">
        <h2 className="text-xl font-semibold text-white">Media Library ({files.length})</h2>
      </div>

      <div className="bg-stone-900/60 border border-stone-800 rounded-xl p-6">
        <p className="text-stone-400 text-sm mb-4">Upload a new image to the library</p>
        <ImageUpload
          value=""
          onChange={(url) => {
            if (url) { toast.success("Image uploaded"); load(); }
          }}
          label="Upload Image"
        />
      </div>

      {files.length === 0 ? (
        <div className="text-center py-12">
          <Image className="w-12 h-12 text-stone-600 mx-auto mb-4" />
          <p className="text-stone-500">No uploaded files yet</p>
        </div>
      ) : (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
          {files.map(f => (
            <div key={f.file_id} className="bg-stone-900/60 border border-stone-800 rounded-xl overflow-hidden group" data-testid={`media-${f.file_id}`}>
              <div className="aspect-square bg-stone-800 flex items-center justify-center overflow-hidden">
                {f.url && (f.content_type || "").startsWith("image") ? (
                  <img src={f.url} alt={f.filename} className="w-full h-full object-cover" />
                ) : (
                  <Image className="w-8 h-8 text-stone-600" />
                )}
              </div>
              <div className="p-3">
                <p className="text-white text-xs truncate" title={f.filename}>{f.filename}</p>
                <p className="text-stone-500 text-xs mt-1">{f.content_type}</p>
                <div className="flex gap-1 mt-2">
                  <Button onClick={() => copyUrl(f.url)} variant="ghost" size="sm" className="text-stone-400 hover:text-white h-7 px-2"><Copy className="w-3 h-3" /></Button>
                  <a href={f.url} target="_blank" rel="noopener noreferrer"><Button variant="ghost" size="sm" className="text-stone-400 hover:text-white h-7 px-2"><ExternalLink className="w-3 h-3" /></Button></a>
                  <Button onClick={() => handleDelete(f.file_id)} variant="ghost" size="sm" className="text-stone-500 hover:text-red-400 h-7 px-2"><Trash2 className="w-3 h-3" /></Button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
